/**
 * Syncs 288 variant picker pill display (thumbnail, label, CSS variables) from the native <select>.
 * Also updates the ingredients accordion when a flavor is selected.
 */

const PICKER_SELECTOR = "variant-picker.variant-picker--288";
const PILL_SELECTOR = "[data-288-picker-pill]";
const SELECT_SELECTOR = "[data-288-variant-select]";

/**
 * Normalize a flavor name for matching: non-breaking spaces → regular spaces,
 * collapsed whitespace, trimmed and lowercased. Metafield-authored names often
 * contain &nbsp; or stray spacing that wouldn't otherwise match the option value
 * (e.g. "Strawberries & Cream" vs "Strawberries & Cream").
 * @param {string | undefined} value
 * @returns {string}
 */
function normalizeFlavorName(value) {
  return (value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Show the matching pre-rendered flavor ingredient item inside [data-flavor-ingredients].
 * Falls back to setting innerHTML when no [data-flavor-name] children exist.
 * @param {HTMLOptionElement} opt - the selected <option>
 * @param {Element | null} section - closest product section root
 */
function updateFlavorIngredients(opt, section) {
  if (!section) return;

  const container = section.querySelector("[data-flavor-ingredients]");
  if (container) {
    const items = container.querySelectorAll("[data-flavor-name]");
    if (items.length > 0) {
      // Pre-rendered flavors path: show/hide by name match
      const flavorName = normalizeFlavorName(opt.value || opt.dataset.label);
      items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;

        if (normalizeFlavorName(item.dataset.flavorName) === flavorName) {
          item.removeAttribute("hidden");
        } else {
          item.setAttribute("hidden", "");
        }
      });
      return;
    }

    // Fallback: replace innerHTML from data-ingredients on the option
    const ingredients = (opt.dataset.ingredients || "").trim();
    if (ingredients) {
      container.innerHTML = `<p class="metafield-multi_line_text_field type-body">${ingredients}</p>`;
      container.removeAttribute("hidden");
    }
    return;
  }

  // Last resort: generic rte-formatter span
  const ingredients = (opt.dataset.ingredients || "").trim();
  const span = section.querySelector(
    "rte-formatter .metafield-multi_line_text_field",
  );
  if (span && ingredients) {
    span.innerHTML = ingredients;
    span.closest("rte-formatter")?.removeAttribute("hidden");
  }
}

/**
 * @param {HTMLSelectElement} select
 */
function sync288VariantPicker(select) {
  if (!(select instanceof HTMLSelectElement)) return;
  if (!select.matches(SELECT_SELECTOR)) return;

  const picker = select.closest(PICKER_SELECTOR);
  if (!picker) return;

  const pill = select.closest(PILL_SELECTOR);
  if (!(pill instanceof HTMLElement)) return;

  const opt = select.options[select.selectedIndex];
  if (!opt) return;

  const bg = (opt.dataset.bg || "").trim();
  const fg = (opt.dataset.fg || "").trim();

  if (bg && fg) {
    pill.style.setProperty("--288-picker-bg", bg);
    pill.style.setProperty("--288-picker-fg", fg);
  } else {
    pill.style.removeProperty("--288-picker-bg");
    pill.style.removeProperty("--288-picker-fg");
  }

  const display = pill.querySelector(".variant-picker-288__display");
  if (!display) return;

  const img = display.querySelector("[data-288-picker-thumb]");
  const labelEl = display.querySelector("[data-288-picker-label]");

  const thumbSrc = (opt.dataset.thumbSrc || "").trim();
  if (img instanceof HTMLImageElement) {
    if (thumbSrc) {
      img.src = thumbSrc;
      img.removeAttribute("hidden");
    } else {
      img.removeAttribute("src");
      img.setAttribute("hidden", "");
    }
  }

  if (labelEl) {
    const labelText = (opt.dataset.label || opt.textContent || "").trim();
    labelEl.textContent = labelText;
  }

  // Media is handled by the native variant picker / product media gallery.
  const section =
    select.closest("section") || select.closest('[id^="shopify-section-"]');
  updateFlavorIngredients(opt, section);
}

function init288VariantPickers() {
  document
    .querySelectorAll(`${PICKER_SELECTOR} ${SELECT_SELECTOR}`)
    .forEach((el) => {
      if (el instanceof HTMLSelectElement) sync288VariantPicker(el);
    });
}

document.addEventListener("change", (event) => {
  const t = event.target;
  if (t instanceof HTMLSelectElement && t.closest(PICKER_SELECTOR)) {
    sync288VariantPicker(t);
  }
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init288VariantPickers, {
    once: true,
  });
} else {
  init288VariantPickers();
}
